import { AGE_BOUND_UNITS, KHIS_SOURCE_KEYS, QUALITY_INDICATOR_KEYS } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema } from './clinical-common.ts';

// KHIS submissions and quality indicators (docs/plans/phase3-khis-and-quality.md §3). Only
// aggregate counts and KHIS identifiers: nothing here names or identifies a patient.

/** `202609`. */
export const khisPeriodSchema = z.string().regex(/^\d{4}(0[1-9]|1[0-2])$/, 'Use YYYYMM');

/** A DHIS2 UID: a letter, then ten letters or digits. */
export const dhis2UidSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z][A-Za-z0-9]{10}$/, 'A KHIS UID is a letter, then 10 letters or digits');

export const khisSourceSchema = z.object({
  key: z.enum(KHIS_SOURCE_KEYS),
  title: z.string(),
  description: z.string(),
});

/** A completed age: days, months or years. */
export const khisAgeBoundSchema = z.object({
  unit: z.enum(AGE_BOUND_UNITS),
  value: z.number().int().min(0).max(73_000),
});

export const khisMappingRowInputSchema = z.object({
  label: z.string().trim().min(1).max(120),
  sourceKey: z.enum(KHIS_SOURCE_KEYS),
  sexCode: z.string().trim().min(1).max(64).nullable(),
  /** Included; null for no lower limit. */
  ageFrom: khisAgeBoundSchema.nullable(),
  /** Not included ("under 5 years"); null for no upper limit. */
  ageBelow: khisAgeBoundSchema.nullable(),
  dataElementUid: dhis2UidSchema,
  categoryOptionComboUid: dhis2UidSchema,
});
export type KhisMappingRowInputView = z.infer<typeof khisMappingRowInputSchema>;

export const khisMappingRowSchema = khisMappingRowInputSchema.extend({
  id: z.uuid(),
  sortOrder: z.number().int(),
});

export const KHIS_MAPPING_STATUSES = ['draft', 'active', 'retired'] as const;

export const khisMappingVersionSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  status: z.enum(KHIS_MAPPING_STATUSES),
  dataSetUid: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
  activatedAt: z.iso.datetime().nullable(),
  activatedBy: actorSchema.nullable(),
  rows: z.array(khisMappingRowSchema),
});
export type KhisMappingVersionView = z.infer<typeof khisMappingVersionSchema>;

export const khisFacilitySchema = z.object({
  facilityId: z.uuid(),
  name: z.string(),
  mflCode: z.string().nullable(),
  orgUnitUid: z.string().nullable(),
});

export const khisMappingViewSchema = z.object({
  facility: khisFacilitySchema,
  active: khisMappingVersionSchema.nullable(),
  draft: khisMappingVersionSchema.nullable(),
  history: z.array(
    z.object({
      id: z.uuid(),
      version: z.number().int(),
      status: z.enum(KHIS_MAPPING_STATUSES),
      activatedAt: z.iso.datetime().nullable(),
    }),
  ),
  sources: z.array(khisSourceSchema),
  /** The patient sex value set's codes, for the sex filter. */
  sexOptions: z.array(z.object({ code: z.string(), display: z.string() })),
});
export type KhisMappingView = z.infer<typeof khisMappingViewSchema>;

export const saveKhisDraftRequestSchema = z.object({
  dataSetUid: dhis2UidSchema.nullable(),
  note: z.string().trim().max(500).nullable(),
  rows: z.array(khisMappingRowInputSchema).max(500),
});
export type SaveKhisDraftRequest = z.infer<typeof saveKhisDraftRequestSchema>;

export const setKhisOrgUnitRequestSchema = z.object({ orgUnitUid: dhis2UidSchema });

/** What a person sees: the stored status, with sending read from the outbox. */
export const KHIS_SUBMISSION_STATES = [
  'prepared',
  'waiting',
  'sent',
  'failed',
  'discarded',
  'superseded',
] as const;
export type KhisSubmissionState = (typeof KHIS_SUBMISSION_STATES)[number];

export const khisSubmissionListItemSchema = z.object({
  id: z.uuid(),
  period: khisPeriodSchema,
  state: z.enum(KHIS_SUBMISSION_STATES),
  preparedAt: z.iso.datetime(),
  preparedBy: actorSchema,
  approvedAt: z.iso.datetime().nullable(),
  approvedBy: actorSchema.nullable(),
  isCorrection: z.boolean(),
});
export type KhisSubmissionListItemView = z.infer<typeof khisSubmissionListItemSchema>;

export const khisSubmissionListSchema = z.object({
  facility: khisFacilitySchema,
  /** Whether a month can be prepared: an active mapping and this facility's org unit. */
  hasActiveMapping: z.boolean(),
  /** The most recent month that has ended; the default to prepare. */
  lastCompletePeriod: khisPeriodSchema,
  items: z.array(khisSubmissionListItemSchema),
});
export type KhisSubmissionListView = z.infer<typeof khisSubmissionListSchema>;

export const khisImportSummarySchema = z.object({
  imported: z.number().int(),
  updated: z.number().int(),
  ignored: z.number().int(),
  deleted: z.number().int(),
  refusedDataElements: z.array(z.string()),
  reference: z.string().nullable(),
});

export const khisSubmissionSchema = khisSubmissionListItemSchema.extend({
  facility: khisFacilitySchema,
  dataSetUid: z.string(),
  orgUnitUid: z.string(),
  mappingVersion: z.number().int(),
  values: z.array(
    z.object({
      label: z.string(),
      dataElementUid: z.string(),
      categoryOptionComboUid: z.string(),
      value: z.number().int(),
      unknownAgeCount: z.number().int(),
    }),
  ),
  /** Why the last send failed, in words, with what to do next. */
  failure: z.object({ code: z.string(), message: z.string(), nextStep: z.string() }).nullable(),
  importSummary: khisImportSummarySchema.nullable(),
  sendAttempts: z.number().int(),
  discardReason: z.string().nullable(),
  correctionReason: z.string().nullable(),
  correctsSubmissionId: z.uuid().nullable(),
  /** The reader prepared it, so cannot approve it (two-person rule). */
  preparedByReader: z.boolean(),
});
export type KhisSubmissionView = z.infer<typeof khisSubmissionSchema>;

export const prepareKhisSubmissionRequestSchema = z.object({
  period: khisPeriodSchema,
  /** Required when the month was already approved: this is a correction. */
  correctionReason: z.string().trim().min(3).max(500).nullable(),
});
export type PrepareKhisSubmissionRequest = z.infer<typeof prepareKhisSubmissionRequestSchema>;

export const discardKhisSubmissionRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

// --- Quality indicators ----------------------------------------------------------------------

export const qualityMonthSchema = z.object({
  period: khisPeriodSchema,
  /** The headline: median minutes or days, a percent or a count. Null with nothing to measure. */
  value: z.number().nullable(),
  /** How many visits, stays, requests, notifications, claims or discharges were measured. */
  sampleSize: z.number().int(),
  /** 9 in 10, for durations. */
  p90: z.number().nullable(),
  meanDays: z.number().nullable(),
  /** Lab: released within the test's own turnaround target, of those with a target. */
  withinTargetPercent: z.number().nullable(),
  /** Critical results: not yet acknowledged, and closed by the lab after phoning. */
  notAcknowledged: z.number().int().nullable(),
  closedByLab: z.number().int().nullable(),
  /** Readmissions: some discharge's 30 days have not passed yet. */
  stillCounting: z.boolean(),
});
export type QualityMonthView = z.infer<typeof qualityMonthSchema>;

export const QUALITY_TARGET_STATUSES = ['met', 'not_met', 'no_target', 'no_value'] as const;

export const qualityIndicatorSchema = z.object({
  key: z.enum(QUALITY_INDICATOR_KEYS),
  /** Oldest first, ending with the chosen month; one entry for stock-outs (now). */
  months: z.array(qualityMonthSchema),
  target: z.number().nullable(),
  targetStatus: z.enum(QUALITY_TARGET_STATUSES),
});
export type QualityIndicatorView = z.infer<typeof qualityIndicatorSchema>;

export const qualityDashboardSchema = z.object({
  facility: z.object({ facilityId: z.uuid(), name: z.string() }),
  period: khisPeriodSchema,
  dataAsAt: z.iso.datetime(),
  indicators: z.array(qualityIndicatorSchema),
});
export type QualityDashboardView = z.infer<typeof qualityDashboardSchema>;

export const setQualityTargetRequestSchema = z.object({
  target: z.number().min(0).max(1_000_000).nullable(),
});
